/**
 * 식단 기록 서버 연동.
 *
 * 두잉랩(FoodLens) SDK 인식 결과와 사진을 서버로 올린다.
 * SDK 호출 자체는 네이티브가 하고(브릿지), 서버 전송은 웹이 한다 —
 * 그래야 JWT 가 웹뷰 밖으로 나가지 않는다.
 */
import axiosApi from './daonAxios';

/**
 * 끼니 구분 공통코드 (tc_cd_info.cd_id).
 * 웹 내부에서는 breakfast/lunch/dinner/snack 을 쓰고, 서버로는 이 코드로 바꿔 보낸다.
 */
export const NTKTM_TY_CD = {
    breakfast: 'BKFST',
    lunch: 'LUNCH',
    dinner: 'DINNR',
    snack: 'SNACK',
};

/**
 * base64(dataUri 포함) -> File
 * 네이티브 normalizeImage 가 준 dataUri 를 multipart 로 보내기 위한 변환.
 */
export const base64ToFile = (
    base64Data,
    fileName = 'doinglab.jpg',
    defaultMimeType = 'image/jpeg',
) => {
    if (!base64Data) {
        throw new Error('이미지 Base64 데이터가 없습니다.');
    }

    let base64 = base64Data;
    let mimeType = defaultMimeType;

    // data:image/jpeg;base64,... 형태인 경우 헤더 분리
    if (base64Data.startsWith('data:')) {
        const commaIndex = base64Data.indexOf(',');
        if (commaIndex < 0) {
            throw new Error('올바르지 않은 Base64 이미지 형식입니다.');
        }
        const header = base64Data.substring(0, commaIndex);
        base64 = base64Data.substring(commaIndex + 1);

        const mimeMatch = header.match(/^data:(.*?);base64$/);
        if (mimeMatch?.[1]) {
            mimeType = mimeMatch[1];
        }
    }

    // 앱에서 줄바꿈이 섞여 넘어오는 경우 제거
    base64 = base64.replace(/\s/g, '');

    const binaryString = window.atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }

    return new File([bytes], fileName, { type: mimeType });
};

/**
 * 두잉랩 인식 결과 + 사진 전송.
 *
 * @param {object}  params
 * @param {string}  params.photoBase64  normalizeImage 가 준 dataUri
 * @param {object}  params.result       SDK predict 원본 JSON (파싱된 객체, 가공하지 않음)
 * @param {string}  params.recordDate   사용자가 보고 있던 날짜 (YYYY-MM-DD)
 * @param {string}  params.mealType     사용자가 누른 끼니 (breakfast/lunch/dinner/snack)
 * @param {number}  params.srcWidth     normalizeImage 반환 width — 박스 좌표 계산 기준
 * @param {number}  params.srcHeight    normalizeImage 반환 height
 * @param {string} [params.fileName]
 * @param {string} [params.mimeType]
 *
 * 주의:
 * - result 는 SDK 원본 그대로 보낸다. 서버 DTO 가 원본 구조를 그대로 미러링하고 있다.
 * - position 은 SDK 가 주는 절대 픽셀값 그대로다. 서버가 비율로 바꿔 저장하려면
 *   반드시 함께 보내는 srcWidth/srcHeight 로 나눠야 한다. 특정 해상도(1536x2048 등)를
 *   상수로 가정하면 다른 기기에서 박스가 어긋난다.
 * - recordDate/mealType 은 SDK 의 date/type 이 아니라 사용자의 선택값이다.
 * - mealType 은 서버로 나갈 때 공통코드(ntktmTyCd = BKFST/LUNCH/DINNR/SNACK)로 바뀐다.
 */
export const uploadDoinglabResult = async ({
    photoBase64,
    result,
    recordDate,
    mealType,
    srcWidth,
    srcHeight,
    fileName,
    mimeType,
}) => {
    if (!photoBase64) throw new Error('사진 데이터가 없습니다.');
    if (!result) throw new Error('두잉랩 분석 결과가 없습니다.');

    const file = base64ToFile(
        photoBase64,
        fileName || `doinglab_${Date.now()}.jpg`,
        mimeType || 'image/jpeg',
    );

    const formData = new FormData();
    // formData.append(
    //     'result',
    //     new Blob([JSON.stringify(result)], { type: 'application/json' }),
    // );
    formData.append('result', JSON.stringify(result));
    formData.append('file', file);
    // 서버가 아직 안 받는 파트들. 컨트롤러가 선언한 파트만 바인딩하고 나머지는 무시하므로
    // 미리 보내둬도 안전하다. 서버 쪽에서 받기 시작하면 웹은 손대지 않아도 된다.
    if (recordDate) formData.append('recDe', recordDate);

    // 서버는 공통코드로만 받는다. 매핑에 없는 값을 그대로 흘려보내면 FK 가 깨지므로 빼고 보낸다.
    const ntktmTyCd = NTKTM_TY_CD[mealType]
        ?? (Object.values(NTKTM_TY_CD).includes(mealType) ? mealType : undefined);
    if (ntktmTyCd) {
        formData.append('ntktmTyCd', ntktmTyCd);
    } else if (mealType) {
        console.warn(`[dietAPI] 알 수 없는 끼니 구분 — ntktmTyCd 미전송 (mealType=${mealType})`);
    }

    if (srcWidth) formData.append('srcWidth', String(srcWidth));
    if (srcHeight) formData.append('srcHeight', String(srcHeight));

    const jToken = localStorage.getItem('jToken');

    const response = await axiosApi.post('/diet/doinglabResult', formData, {
        headers: {
            // axios 인스턴스 기본값이 application/json 이라 지워야 multipart boundary 가 붙는다.
            'Content-Type': undefined,
            Authorization: `Bearer ${jToken}`,
        },
    });

    return response.data;
};
